import {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from 'react';
import { format, differenceInDays, subMonths } from 'date-fns';
import { Bill, useAuth } from './AuthContext';



interface IFinancialStatusContext {
  isOverdue: boolean;
  isLoading: boolean;
  overdueBill: Bill | null;
}

const FinancialStatusContext = createContext<IFinancialStatusContext>({} as IFinancialStatusContext);

const FinancialStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, licenseeBills } = useAuth();

  const [isOverdue, setIsOverdue] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [overdueBill, setOverdueBill] = useState<Bill | null>(null);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    async function checkBillsStatus() {
      const today = new Date();
      const currentDayOfMonth = today.getDate();

      if (currentDayOfMonth < 10) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const bills = licenseeBills;

        const lastMonth = subMonths(today, 1);

        const targetPeriod = format(lastMonth, 'M/yyyy');

        const relevantBill = bills.find(
          (bill) => bill.referring_period === targetPeriod
        );

        if (!relevantBill) {
          setIsLoading(false);
          return;
        }

        const isPaid = !!relevantBill.payment_date ||
          relevantBill.payment_situation === 'PAYMENT_RECEIVED' ||
          relevantBill.payment_situation === 'PAYMENT_CONFIRMED';

        if (isPaid) {
          setIsLoading(false);
          return;
        }

        const dueDate = new Date(relevantBill.due_date);
        const daysOverdue = differenceInDays(today, dueDate);

        if (daysOverdue > 5) {
          setIsOverdue(true);
          setOverdueBill(relevantBill);
        }

      } catch (error) {
        console.error("Erro ao verificar status financeiro:", error);
      } finally {
        setIsLoading(false);
      }
    }
    checkBillsStatus();
  }, [user, licenseeBills]);

  return (
    <FinancialStatusContext.Provider value={{ isOverdue, isLoading, overdueBill }}>
      {children}
    </FinancialStatusContext.Provider>
  );
};

function useFinancialStatus() {
  const context = useContext(FinancialStatusContext);

  if (!context) {
    throw new Error("useFinancialStatus must be used within a FinancialStatusProvider");
  }

  return context;
}

export { useFinancialStatus, FinancialStatusProvider };
